/* eslint-disable import/no-cycle */
import { createSelector } from 'reselect';
import { EMPTY_OBJECT } from './constants';
import { isDefined } from './util';
import { selectSurvey } from '../../../../selectors/surveys';
import { getSurveyIdFromChartId } from '../../../../../utils/chart';
import SurveyMeta from '../../../../../utils/SurveyMeta';

const selectScanlineMain = (state) => {
	const stateApp = state?.app || state;

	return stateApp;
}

export const selectChartsSettings = (state = EMPTY_OBJECT) => {
	const stateApp = selectScanlineMain(state);
	const { chartsSettings = EMPTY_OBJECT } = stateApp;
	return chartsSettings;
};

export const selectChartSettings = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartsSettings = selectChartsSettings(stateApp);
	const chartSettings = chartsSettings[chartId] || EMPTY_OBJECT;

	return chartSettings;
};

export const selectThresholds = (state, { chartId } = {}) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { lessNegativeThreshold, moreNegativeThreshold } = chartSettings;

	return { lessNegativeThreshold, moreNegativeThreshold };
};


export const selectLessNegativeThreshold = (state, { chartId, ignoreFallback = false } = {}) => {
	const stateApp = selectScanlineMain(state);
	const survey = selectSurvey(stateApp, getSurveyIdFromChartId(chartId));
	const surveyMeta = survey ? SurveyMeta.getSurveyMeta(survey) : undefined;
	const chartSettings = selectChartSettings(stateApp, { chartId });

	return [
		chartSettings?.lessNegativeThreshold,
		!ignoreFallback ? surveyMeta?.lessNegativeThreshold : undefined
	].filter(v => isDefined(v))[0];
}

export const selectMoreNegativeThreshold = (state, { chartId, ignoreFallback = false } = {}) => {
	const stateApp = selectScanlineMain(state);
	const survey = selectSurvey(stateApp, getSurveyIdFromChartId(chartId));
	const surveyMeta = survey ? SurveyMeta.getSurveyMeta(survey) : undefined;
	const chartSettings = selectChartSettings(stateApp, { chartId });

	return [
		chartSettings?.moreNegativeThreshold,
		!ignoreFallback ? surveyMeta?.moreNegativeThreshold : undefined
	].filter(v => isDefined(v))[0];
}

export const selectShowDecimatedData = (state, { chartId } = {}) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { showDecimatedData } = chartSettings;
	return showDecimatedData;
};

export const selectRequiresThresholdChangeConfirmation = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { thresholdsPendingChangeRequiresConfirmation } = chartSettings;

	return !!thresholdsPendingChangeRequiresConfirmation;
};

export const selectUseThreshold = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { useThreshold } = chartSettings;

	return !!useThreshold;
};

export const selectUseShadow = (state = EMPTY_OBJECT, { chartId } = {}) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { useShadow } = chartSettings;

	return !!useShadow;
};

export const selectUseSawtooth = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { useSawtooth } = chartSettings;

	return !!useSawtooth;
};

export const selectHasSawtoothData = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { hasSawtoothData } = chartSettings;

	return !!hasSawtoothData;
};

export const selectChartPrimarySurveyId = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { primarySurveyId } = chartSettings;

	if (!primarySurveyId) {
		// todo - need fallback functionality here for when chart settings is not set from backend - and primarySurveyId is not stored in settings.
		// debugger;
	}

	return primarySurveyId;
};

export const selectChartPrimarySurvey = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const primarySurveyId = selectChartPrimarySurveyId(stateApp, { chartId });
	return selectSurvey(stateApp, primarySurveyId);
};

export const selectChartDepolSurveyId = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { depolSurveyId } = chartSettings;

	return depolSurveyId;
};

export const selectChartDepolSurvey = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const depolSurveyId = selectChartDepolSurveyId(stateApp, { chartId });
	return selectSurvey(stateApp, depolSurveyId);
};

export const selectUseInterpolatedDepol = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { useInterpolatedDepol } = chartSettings;

	return useInterpolatedDepol;
};

export const selectUseNormalizedDepol = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { useNormalizedDepol } = chartSettings;

	return useNormalizedDepol;
};

// @deprecated - this is required due to previous arch combined with readings table modal implementation being inline in Map.jsx
export const selectUseNormalizedDepolMap = createSelector(
	selectChartsSettings,
	chartsSettings => {
		const chartIds = Object.keys(chartsSettings);
		const useNormalizedDepolMap = chartIds.reduce((acc, chartId) => {
			const chartSettings = chartsSettings[chartId];
			const { primarySurveyId, useNormalizedDepol } = chartSettings;
			acc[primarySurveyId] = useNormalizedDepol;
			return acc;
		}, {});
		return useNormalizedDepolMap;
	}
);

// @deprecated - this is required due to previous arch combined with readings table modal implementation being inline in Map.jsx
export const selectUseInterpolatedDepolMap = createSelector(
	selectChartsSettings,
	chartsSettings => {
		const chartIds = Object.keys(chartsSettings);
		const useInterpolatedDepolMap = chartIds.reduce((acc, chartId) => {
			const chartSettings = chartsSettings[chartId];
			const { primarySurveyId, useInterpolatedDepol } = chartSettings;
			acc[primarySurveyId] = useInterpolatedDepol;
			return acc;
		}, {});
		return useInterpolatedDepolMap;
	}
);

export const selectSelectedRemediationGroup = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { selectedRemediationGroup } = chartSettings;

	return selectedRemediationGroup;
};

export const selectSelectedRemediationGroupIndex = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { selectedRemediationGroupIndex } = chartSettings;

	return selectedRemediationGroupIndex;
};

export const selectInputThresholdResetToken = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { inputThresholdResetToken } = chartSettings;
	return inputThresholdResetToken;
};

export const selectSeriesVisibilityResetToken = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { seriesVisibilityResetToken } = chartSettings;
	return seriesVisibilityResetToken;
};

export const selectSeriesVisibilityDataStoreForChart = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { seriesVisibility } = chartSettings;
	return seriesVisibility;
};

export const selectSeriesVisibility = (
	state = EMPTY_OBJECT,
	{ chartId } = {},
	{
		dataSetKey, // could be a survey id - could be the name of a series
		propKey // name of property which is plotted on chart
	}
) => {
	const stateApp = selectScanlineMain(state);
	const seriesVisibilityDataStore = selectSeriesVisibilityDataStoreForChart(
		stateApp,
		{ chartId }
	);

	const isVisible = ((seriesVisibilityDataStore || {})[dataSetKey] || {})[
		propKey
	];

	return isDefined(isVisible) ? isVisible : true; // for now assume is visible
};

export const selectIsVisible = (state = EMPTY_OBJECT, { chartId } = {}) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { isVisible } = chartSettings;

	return isVisible;
};

export const selectShowSpatial = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { showSpatial } = chartSettings;

	return showSpatial;
};

export const selectBelowCriterionComparator = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { belowCriterionComparator } = chartSettings;
	return belowCriterionComparator;
};

export const selectShowConfirmation = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { showConfirmation } = chartSettings;

	return showConfirmation;
};

export const selectConfirmationMessageKey = (
	state = EMPTY_OBJECT,
	{ chartId } = {}
) => {
	const stateApp = selectScanlineMain(state);
	const chartSettings = selectChartSettings(stateApp, { chartId });
	const { confirmationMessageKey } = chartSettings;

	return confirmationMessageKey;
};

export default {
	selectChartSettings,
	selectUseSawtooth,
	selectChartPrimarySurveyId,
	selectChartPrimarySurvey,
	selectChartDepolSurveyId,
	selectChartDepolSurvey,
	selectUseNormalizedDepol,
	selectUseInterpolatedDepol,
	selectSeriesVisibilityDataStoreForChart,
	selectSeriesVisibility,
	selectIsVisible,
	selectUseNormalizedDepolMap,
	selectUseInterpolatedDepolMap,
	selectShowConfirmation,

	selectUseThreshold,
	selectUseShadow,
	selectThresholds,
	selectLessNegativeThreshold,
	selectMoreNegativeThreshold,
};
